import { useCallback, useState } from "react";
import {
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { toast } from "sonner";
import {
  createElement,
  createCustomElement,
  ELEMENT_TYPES,
  SECTION_ORDER,
} from "@/lib/elements";
import { snippetApi } from "@/lib/api";

/**
 * Random id generator for fresh canvas elements. Centralised so duplicate /
 * snippet-drop / element-drop all use the same shape.
 */
function freshElementId(suffix = "") {
  return `el-${Date.now()}-${Math.random().toString(36).slice(2, 9)}${suffix ? `-${suffix}` : ""}`;
}

/**
 * Deep-clone an element via JSON round-trip. Used by duplicate/save-snippet
 * to detach the structural data from any in-memory shared refs.
 */
function cloneElement(el) {
  return JSON.parse(JSON.stringify(el));
}

/**
 * useDesignerCallbacks — bundles every element / snippet / drag-and-drop
 * callback that DesignerPage previously owned inline. Returned as a single
 * object so the consumer destructures whatever it needs.
 *
 * Inputs: setters for sections, selection, snippets, plus refs to the latest
 * `sections` / `selectedIds` / element-defaults (read inside callbacks).
 *
 * No behavioral change vs. the original inline implementations — same
 * dependency arrays, same state-update patterns, same StrictMode-safe
 * cloning patterns for bulk duplicate.
 */
export function useDesignerCallbacks({
  sections,
  setSections,
  selectedIds,
  setSelectedIds,
  setSnippets,
  elementDefaultsRef,
}) {
  // ── DnD state ────────────────────────────────────────────────────────────
  const [activeDragItem, setActiveDragItem] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // ── Section lookup helper ───────────────────────────────────────────────
  const findSectionForId = useCallback(
    (id) => {
      for (const key of SECTION_ORDER) {
        if (sections[key].some((el) => el.id === id)) return key;
      }
      return null;
    },
    [sections]
  );

  // ── Element CRUD ────────────────────────────────────────────────────────
  const deleteElement = useCallback((id) => {
    setSections((prev) => {
      const updated = {};
      for (const key of SECTION_ORDER) {
        updated[key] = prev[key].filter((el) => el.id !== id);
      }
      return updated;
    });
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  }, [setSections, setSelectedIds]);

  const deleteSelectedElements = useCallback(() => {
    if (selectedIds.length === 0) return;
    setSections((prev) => {
      const updated = {};
      for (const key of SECTION_ORDER) {
        updated[key] = prev[key].filter((el) => !selectedIds.includes(el.id));
      }
      return updated;
    });
    setSelectedIds([]);
  }, [selectedIds, setSections, setSelectedIds]);

  const updateElement = useCallback((id, newProps) => {
    setSections((prev) => {
      const updated = {};
      for (const key of SECTION_ORDER) {
        updated[key] = prev[key].map((el) =>
          el.id === id ? { ...el, props: { ...el.props, ...newProps } } : el
        );
      }
      return updated;
    });
  }, [setSections]);

  const duplicateElement = useCallback((id) => {
    const section = findSectionForId(id);
    if (!section) return;
    const el = sections[section].find((e) => e.id === id);
    if (!el) return;
    const newEl = { ...cloneElement(el), id: freshElementId() };
    setSections((prev) => {
      const items = [...prev[section]];
      const idx = items.findIndex((e) => e.id === id);
      items.splice(idx + 1, 0, newEl);
      return { ...prev, [section]: items };
    });
    setSelectedIds([newEl.id]);
  }, [sections, findSectionForId, setSections, setSelectedIds]);

  /**
   * Bulk duplicate. Pre-computes clones OUTSIDE the state updater so the
   * function stays pure under React.StrictMode (which invokes updaters twice
   * in dev) and concurrent rendering. Each source maps to one clone with a
   * stable pre-generated id.
   */
  const duplicateSelectedElements = useCallback(() => {
    if (selectedIds.length === 0) return;

    const cloneByOriginalId = new Map();
    for (const key of SECTION_ORDER) {
      for (const el of sections[key]) {
        if (selectedIds.includes(el.id)) {
          cloneByOriginalId.set(el.id, {
            ...cloneElement(el),
            id: freshElementId(String(cloneByOriginalId.size)),
          });
        }
      }
    }
    if (cloneByOriginalId.size === 0) return;

    setSections((prev) => {
      const updated = {};
      for (const key of SECTION_ORDER) {
        const items = [];
        for (const el of prev[key]) {
          items.push(el);
          const clone = cloneByOriginalId.get(el.id);
          if (clone) items.push(clone);
        }
        updated[key] = items;
      }
      return updated;
    });

    setSelectedIds(Array.from(cloneByOriginalId.values()).map((c) => c.id));
  }, [selectedIds, sections, setSections, setSelectedIds]);

  const moveElementToSection = useCallback((id, targetSection) => {
    setSections((prev) => {
      let sourceSection = null;
      for (const key of SECTION_ORDER) {
        if (prev[key].some((e) => e.id === id)) {
          sourceSection = key;
          break;
        }
      }
      if (!sourceSection || sourceSection === targetSection) return prev;
      const el = prev[sourceSection].find((e) => e.id === id);
      if (!el) return prev;
      return {
        ...prev,
        [sourceSection]: prev[sourceSection].filter((e) => e.id !== id),
        [targetSection]: [...prev[targetSection], el],
      };
    });
  }, [setSections]);

  // ── Snippet operations ──────────────────────────────────────────────────
  const handleSaveAsSnippet = useCallback(async (element, sectionHint) => {
    if (!element) return;
    const defaultName = element.label || ELEMENT_TYPES[element.type]?.label || "Snippet";
    const name = window.prompt("Snippet name:", defaultName);
    if (!name) return;
    try {
      const clean = cloneElement(element);
      const created = await snippetApi.create({
        name: name.trim(),
        description: "",
        icon: ELEMENT_TYPES[element.type]?.icon || "Bookmark",
        sectionHint: sectionHint || "body",
        elements: [clean],
      });
      setSnippets((prev) => [...prev, created]);
      toast.success(`Saved "${created.name}" as snippet`);
    } catch {
      toast.error("Failed to save snippet");
    }
  }, [setSnippets]);

  const handleSaveSelectionAsSnippet = useCallback(async () => {
    if (selectedIds.length === 0) return;

    const orderedElements = [];
    let sectionHint = "body";
    for (const key of SECTION_ORDER) {
      for (const el of sections[key]) {
        if (selectedIds.includes(el.id)) {
          if (orderedElements.length === 0) sectionHint = key;
          orderedElements.push(cloneElement(el));
        }
      }
    }
    if (orderedElements.length === 0) return;

    const defaultName = `Group of ${orderedElements.length}`;
    const name = window.prompt("Snippet name:", defaultName);
    if (!name) return;

    try {
      const created = await snippetApi.create({
        name: name.trim(),
        description: "",
        icon: "Bookmark",
        sectionHint,
        elements: orderedElements,
      });
      setSnippets((prev) => [...prev, created]);
      toast.success(`Saved "${created.name}" as snippet (${orderedElements.length} elements)`);
    } catch {
      toast.error("Failed to save snippet");
    }
  }, [selectedIds, sections, setSnippets]);

  const handleDeleteSnippet = useCallback(async (snippet) => {
    if (!window.confirm(`Delete snippet "${snippet.name}"?`)) return;
    try {
      await snippetApi.delete(snippet.id);
      setSnippets((prev) => prev.filter((s) => s.id !== snippet.id));
      toast.success("Snippet deleted");
    } catch {
      toast.error("Failed to delete snippet");
    }
  }, [setSnippets]);

  // ── Drag-and-drop handlers ─────────────────────────────────────────────
  const handleDragStart = useCallback((event) => {
    setActiveDragItem(event.active);
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveDragItem(null);
    if (!over) return;

    const overId = String(over.id);

    if (active.data.current?.fromToolbox) {
      // Snippets: a snippet expands into 1..N elements, each given a fresh id.
      if (active.data.current.elementType === "snippet") {
        const snippet = active.data.current.snippetDef;
        const fresh = (snippet.elements || []).map((el) => ({
          ...cloneElement(el),
          id: `el-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`,
        }));
        if (fresh.length === 0) return;

        let snippetTargetSection = snippet.sectionHint || "body";
        let snippetInsertIndex = -1;

        if (overId.startsWith("section-")) {
          snippetTargetSection = overId.replace("section-", "");
        } else {
          for (const key of SECTION_ORDER) {
            const idx = sections[key].findIndex((e) => e.id === over.id);
            if (idx >= 0) {
              snippetTargetSection = key;
              snippetInsertIndex = idx;
              break;
            }
          }
        }

        setSections((prev) => {
          const items = [...prev[snippetTargetSection]];
          if (snippetInsertIndex >= 0) {
            items.splice(snippetInsertIndex, 0, ...fresh);
          } else {
            items.push(...fresh);
          }
          return { ...prev, [snippetTargetSection]: items };
        });
        setSelectedIds(fresh.map((e) => e.id));
        return;
      }

      let newEl;
      if (active.data.current.elementType === "custom") {
        newEl = createCustomElement(active.data.current.customElementDef);
      } else {
        newEl = createElement(active.data.current.elementType);
        const overrides = elementDefaultsRef.current[active.data.current.elementType] || {};
        if (Object.keys(overrides).length > 0) {
          newEl = { ...newEl, props: { ...newEl.props, ...overrides } };
        }
      }

      let targetSection = "body";
      let insertIndex = -1;

      if (overId.startsWith("section-")) {
        targetSection = overId.replace("section-", "");
      } else {
        for (const key of SECTION_ORDER) {
          const idx = sections[key].findIndex((e) => e.id === over.id);
          if (idx >= 0) {
            targetSection = key;
            insertIndex = idx;
            break;
          }
        }
      }

      setSections((prev) => {
        const items = [...prev[targetSection]];
        if (insertIndex >= 0) {
          items.splice(insertIndex, 0, newEl);
        } else {
          items.push(newEl);
        }
        return { ...prev, [targetSection]: items };
      });
      setSelectedIds([newEl.id]);
    } else {
      if (overId.startsWith("section-") || active.id === over.id) return;

      let sourceSection = null;
      let targetSection = null;
      for (const key of SECTION_ORDER) {
        if (sections[key].some((e) => e.id === active.id)) sourceSection = key;
        if (sections[key].some((e) => e.id === over.id)) targetSection = key;
      }

      if (sourceSection && sourceSection === targetSection) {
        setSections((prev) => {
          const items = [...prev[sourceSection]];
          const oldIdx = items.findIndex((e) => e.id === active.id);
          const newIdx = items.findIndex((e) => e.id === over.id);
          if (oldIdx >= 0 && newIdx >= 0) {
            return { ...prev, [sourceSection]: arrayMove(items, oldIdx, newIdx) };
          }
          return prev;
        });
      }
    }
  }, [sections, setSections, setSelectedIds, elementDefaultsRef]);

  return {
    // dnd
    activeDragItem,
    sensors,
    handleDragStart,
    handleDragEnd,
    // element ops
    findSectionForId,
    deleteElement,
    deleteSelectedElements,
    updateElement,
    duplicateElement,
    duplicateSelectedElements,
    moveElementToSection,
    // snippet ops
    handleSaveAsSnippet,
    handleSaveSelectionAsSnippet,
    handleDeleteSnippet,
  };
}
